import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Alert, Spinner, Modal, Form } from 'react-bootstrap';
import { supplierAPI } from '../utils/api';

const SupplierManagement = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal states
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    contactPerson: '',
    phone: '',
    email: '',
    isActive: true
  });
  const [processing, setProcessing] = useState(false);

  // Ref to prevent duplicate API calls
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadSuppliers();
    }
  }, []);

  const loadSuppliers = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await supplierAPI.getAll();
      setSuppliers(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClick = () => {
    setModalMode('create');
    setFormData({
      code: '',
      name: '',
      contactPerson: '',
      phone: '',
      email: '',
      isActive: true
    });
    setShowSupplierModal(true);
  };

  const handleEditClick = (supplier) => {
    setModalMode('edit');
    setSelectedSupplier(supplier);
    setFormData({
      code: supplier.code,
      name: supplier.name,
      contactPerson: supplier.contactPerson || '',
      phone: supplier.phone || '',
      email: supplier.email || '',
      isActive: supplier.isActive
    });
    setShowSupplierModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setError('');

    try {
      if (modalMode === 'create') {
        await supplierAPI.create(formData);
        setSuccess('สร้างซัพพลายเออร์สำเร็จ');
      } else {
        await supplierAPI.update(selectedSupplier.id, formData);
        setSuccess('อัปเดตข้อมูลซัพพลายเออร์สำเร็จ');
      }
      setShowSupplierModal(false);
      loadSuppliers();
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาด');
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async (supplierId) => {
    if (!window.confirm('ต้องการลบซัพพลายเออร์นี้หรือไม่? หากมีการบันทึกสต็อกที่เกี่ยวข้องจะไม่สามารถลบได้')) {
      return;
    }

    try {
      await supplierAPI.delete(supplierId);
      setSuccess('ลบซัพพลายเออร์สำเร็จ');
      loadSuppliers();
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการลบซัพพลายเออร์');
    }
  };

  const handleCloseModal = () => {
    setShowSupplierModal(false);
    setSelectedSupplier(null);
  };

  return (
    <Container fluid className="main-content">
      <Row className="mb-4">
        <Col>
          <h2>
            <i className="bi bi-truck me-2"></i>
            จัดการซัพพลายเออร์
          </h2>
          <p className="text-muted">จัดการข้อมูลซัพพลายเออร์และผู้จัดส่ง</p>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={handleCreateClick}>
            <i className="bi bi-plus-circle me-2"></i>
            สร้างซัพพลายเออร์ใหม่
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card>
        <Card.Header>
          <h5 className="mb-0">รายการซัพพลายเออร์</h5>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">กำลังโหลดข้อมูล...</p>
            </div>
          ) : suppliers.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-inbox display-1 text-muted"></i>
              <p className="text-muted mt-2">ยังไม่มีซัพพลายเออร์ในระบบ</p>
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>รหัส</th>
                  <th>ชื่อ</th>
                  <th>ผู้ติดต่อ</th>
                  <th>เบอร์โทร</th>
                  <th>อีเมล</th>
                  <th>สถานะ</th>
                  <th className="text-center">การจัดการ</th>
                </tr>
              </thead>
              <tbody>
                {suppliers.map((supplier) => (
                  <tr key={supplier.id}>
                    <td><strong>{supplier.code}</strong></td>
                    <td>{supplier.name}</td>
                    <td>{supplier.contactPerson || '-'}</td>
                    <td>{supplier.phone || '-'}</td>
                    <td>{supplier.email || '-'}</td>
                    <td>
                      {supplier.isActive ? (
                        <Badge bg="success">ใช้งาน</Badge>
                      ) : (
                        <Badge bg="secondary">ไม่ใช้งาน</Badge>
                      )}
                    </td>
                    <td className="text-center">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        onClick={() => handleEditClick(supplier)}
                      >
                        <i className="bi bi-pencil"></i> แก้ไข
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(supplier.id)}
                      >
                        <i className="bi bi-trash"></i> ลบ
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Supplier Create/Edit Modal */}
      <Modal show={showSupplierModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {modalMode === 'create' ? 'สร้างซัพพลายเออร์ใหม่' : 'แก้ไขซัพพลายเออร์'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>รหัสซัพพลายเออร์ <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="เช่น SUP001"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    required
                    disabled={processing}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>ชื่อซัพพลายเออร์ <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="ชื่อร้านค้า/บริษัท"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    disabled={processing}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>ผู้ติดต่อ</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="ชื่อผู้ติดต่อ"
                    value={formData.contactPerson}
                    onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                    disabled={processing}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>เบอร์โทร</Form.Label>
                  <Form.Control
                    type="tel"
                    placeholder="เบอร์โทรศัพท์"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    disabled={processing}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>อีเมล</Form.Label>
              <Form.Control
                type="email"
                placeholder="email@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={processing}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="ใช้งาน (Active)"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                disabled={processing}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal} disabled={processing}>
              ยกเลิก
            </Button>
            <Button variant="primary" type="submit" disabled={processing}>
              {processing ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  กำลังบันทึก...
                </>
              ) : (
                <>
                  <i className="bi bi-check-circle me-2"></i>
                  บันทึก
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default SupplierManagement;
